import React, { useState, useEffect } from 'react';
import { Download, X, User, Building2, Mail, Phone, DollarSign, TrendingUp } from 'lucide-react';
import { API_URL } from '../config';
import { useDateFormatter } from '../utils/dateUtils';
import './LeadsModal.css';

const LeadsModal = ({ isOpen, onClose }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const { formatDateTime } = useDateFormatter();

  useEffect(() => {
    if (isOpen) {
      fetchLeads();
    }
  }, [isOpen]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/advisor/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data = await response.json();
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (sessionToken, leadName) => {
    try {
      setDownloadingPDF(sessionToken);

      const response = await fetch(`${API_URL}/public/lead-assessment/pdf/${sessionToken}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Exit_Readiness_Report_${leadName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="leads-modal" onClick={(e) => e.stopPropagation()}>
        <div className="leads-modal-header">
          <h2>Exit Readiness Assessment Leads</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="leads-modal-body">
          {loading && (
            <div className="leads-loading">
              <div className="spinner" />
              <p>Loading leads...</p>
            </div>
          )}

          {error && (
            <div className="leads-error">
              <p>Error: {error}</p>
              <button onClick={fetchLeads} className="btn-retry">Retry</button>
            </div>
          )}

          {!loading && !error && leads.length === 0 && (
            <div className="leads-empty">
              <p>No assessment leads found yet.</p>
            </div>
          )}

          {!loading && !error && leads.length > 0 && (
            <div className="leads-table-container">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Business</th>
                    <th>Contact</th>
                    <th>Revenue</th>
                    <th>EBITDA</th>
                    <th>Industry</th>
                    <th>Net Worth</th>
                    <th>Wealth Goal</th>
                    <th>Exit Score</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <div className="lead-info">
                          <User size={16} />
                          <span>{lead.full_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="lead-info">
                          <Building2 size={16} />
                          <span>{lead.business_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="lead-contact">
                          <div className="contact-item">
                            <Mail size={14} />
                            <a href={`mailto:${lead.email}`}>{lead.email}</a>
                          </div>
                          {lead.phone && (
                            <div className="contact-item">
                              <Phone size={14} />
                              <a href={`tel:${lead.phone}`}>{lead.phone}</a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{formatCurrency(lead.assessment?.annual_revenue)}</td>
                      <td>{formatCurrency(lead.assessment?.ebitda_amount)}</td>
                      <td>{lead.assessment?.industry || 'N/A'}</td>
                      <td>{formatCurrency(lead.assessment?.current_net_worth)}</td>
                      <td>{formatCurrency(lead.assessment?.desired_exit_proceeds)}</td>
                      <td>
                        <div className="score-badge">
                          <TrendingUp size={14} />
                          {lead.assessment?.overall_exit_readiness_score ?
                            `${Math.round(lead.assessment.overall_exit_readiness_score)}%` :
                            'N/A'
                          }
                        </div>
                      </td>
                      <td>{lead.assessment?.completed_at ? formatDateTime(lead.assessment.completed_at) : 'N/A'}</td>
                      <td>
                        <button
                          className="btn-download-pdf"
                          onClick={() => handleDownloadPDF(lead.assessment?.session_token, lead.full_name)}
                          disabled={downloadingPDF === lead.assessment?.session_token}
                        >
                          {downloadingPDF === lead.assessment?.session_token ? (
                            <>
                              <div className="btn-spinner" />
                              <span>Downloading...</span>
                            </>
                          ) : (
                            <>
                              <Download size={16} />
                              <span>PDF</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="leads-modal-footer">
          <p className="leads-count">Total Leads: {leads.length}</p>
          <button className="btn-secondary min-h-[44px]" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default LeadsModal;
